import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Radio, Wallet, Clock, Users, TrendingUp, Bell, CalendarPlus, Trash2, CalendarClock } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const PRESENCE = { online: "Disponible", busy: "En consultation", offline: "Hors ligne" };

export default function ExpertDashboard() {
  const { user, subscribe } = useAuth();
  const [profile, setProfile] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [presence, setPresence] = useState("offline");
  const [payoutAmount, setPayoutAmount] = useState("");
  const [newSlot, setNewSlot] = useState("");
  const [bookings, setBookings] = useState([]);
  const navigate = useNavigate();

  const loadAll = useCallback(() => {
    api.myExpertProfile().then(({ data }) => { setProfile(data); setPresence(data.presence); }).catch(() => {});
    api.myEarnings().then(({ data }) => setEarnings(data)).catch(() => {});
    api.listConsultations().then(({ data }) => setConsultations(data)).catch(() => {});
    api.myBookings().then(({ data }) => setBookings(data)).catch(() => {});
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => subscribe((msg) => {
    if (msg.type === "consultation_request") {
      toast.success(`Nouvelle demande de ${msg.consultation.consultant_name} !`, { duration: 8000 });
      loadAll();
    }
    if (msg.type === "session_ended") loadAll();
  }), [subscribe, loadAll]);

  const changePresence = async (state) => {
    try { await api.setPresence(state); setPresence(state); toast.success(`Statut : ${PRESENCE[state]}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const requestPayout = async () => {
    const amt = parseFloat(payoutAmount);
    if (!amt || amt <= 0) { toast.error("Montant invalide"); return; }
    try { await api.requestPayout(amt); toast.success("Demande de retrait envoyée."); setPayoutAmount(""); loadAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addSlot = async () => {
    if (!newSlot) { toast.error("Choisissez une date et une heure."); return; }
    try { await api.addSlot(new Date(newSlot).toISOString()); toast.success("Créneau ajouté."); setNewSlot(""); loadAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeSlot = async (sid) => { try { await api.removeSlot(sid); loadAll(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const pending = consultations.filter((c) => c.status === "pending");

  if (!profile) return (
    <div className="mx-auto max-w-3xl px-4 py-20 text-center">
      <p className="text-celeste-lilas">Vous n'avez pas encore de profil expert approuvé.</p>
    </div>
  );

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="font-serif text-3xl text-celeste-ivoire">Espace praticien</h1>
          <p className="mt-1 text-sm text-celeste-lilas/70">{profile.professional_name} · {profile.title}</p>
        </div>
        <div className="flex items-center gap-2">
          <Radio className="h-4 w-4 text-celeste-or" />
          <Select value={presence} onValueChange={changePresence}>
            <SelectTrigger data-testid="presence-select" className="w-44 bg-celeste-night/50 border-celeste-or/30 text-celeste-ivoire"><SelectValue /></SelectTrigger>
            <SelectContent className="glass-strong text-celeste-ivoire">
              <SelectItem value="online">🟢 Disponible</SelectItem>
              <SelectItem value="busy">🟠 En consultation</SelectItem>
              <SelectItem value="offline">⚪ Hors ligne</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="glass mt-6 rounded-3xl border border-celeste-or/25 p-5" data-testid="founding-card">
        {profile.founding_member ? (
          <>
            <h2 className="font-serif text-lg text-celeste-or">✦ Membre fondateur</h2>
            <p className="mt-1 text-sm text-celeste-lilas/85">Vous faites partie des 20 praticiens fondateurs : <strong>55 % du montant des consultations</strong> vous sont reversés (avant frais de paiement). Exemple : 1 €/min → 0,55 € ; 2 €/min → 1,10 €. Avantage conservé tant que vous restez inscrit ; une déconnexion, absence ou pause ne le supprime pas.</p>
          </>
        ) : (
          <>
            <h2 className="font-serif text-lg text-celeste-or">Offre fondateurs</h2>
            <p className="mt-1 text-sm text-celeste-lilas/85">Les 20 premiers praticiens réels bénéficient de <strong>55 % reversés</strong> (au lieu du taux standard), attribués à la validation de l'inscription (hors profils de démonstration). Renseignez-vous auprès de l'administration.</p>
          </>
        )}
      </div>

      {pending.length > 0 && (
        <div className="glass mt-6 rounded-3xl border border-celeste-or/40 p-5" data-testid="incoming-requests">
          <h2 className="flex items-center gap-2 font-serif text-lg text-celeste-or"><Bell className="h-5 w-5 animate-pulse" /> Demandes entrantes</h2>
          <div className="mt-3 space-y-2">
            {pending.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-2xl bg-celeste-night/50 px-4 py-3">
                <p className="text-sm text-celeste-ivoire">{c.consultant_name} souhaite vous consulter</p>
                <Button data-testid={`join-${c.id}`} size="sm" className="gold-btn font-semibold" onClick={() => navigate(`/chat/${c.id}`)}>Rejoindre</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 grid gap-4 sm:grid-cols-4">
        <div className="glass rounded-3xl p-5"><TrendingUp className="h-6 w-6 text-celeste-or" /><p className="mt-2 font-serif text-2xl text-celeste-or">{earnings?.total_earnings ?? 0}</p><p className="text-xs text-celeste-lilas/70">crédits gagnés</p></div>
        <div className="glass rounded-3xl p-5"><Wallet className="h-6 w-6 text-celeste-or" /><p className="mt-2 font-serif text-2xl text-celeste-ivoire">{earnings?.available ?? 0}</p><p className="text-xs text-celeste-lilas/70">disponibles au retrait</p></div>
        <div className="glass rounded-3xl p-5"><Clock className="h-6 w-6 text-celeste-or" /><p className="mt-2 font-serif text-2xl text-celeste-ivoire">{earnings?.total_minutes ?? 0}</p><p className="text-xs text-celeste-lilas/70">minutes</p></div>
        <div className="glass rounded-3xl p-5"><Users className="h-6 w-6 text-celeste-or" /><p className="mt-2 font-serif text-2xl text-celeste-ivoire">{earnings?.consultations ?? 0}</p><p className="text-xs text-celeste-lilas/70">consultations</p></div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="glass rounded-3xl p-6">
          <h2 className="font-serif text-lg text-celeste-ivoire">Demander un retrait</h2>
          <p className="mt-1 text-xs text-celeste-lilas/60">Solde disponible : {earnings?.available ?? 0} crédits.</p>
          <div className="mt-4 flex gap-2">
            <Input data-testid="payout-amount" type="number" value={payoutAmount} onChange={(e) => setPayoutAmount(e.target.value)} placeholder="Montant en crédits" className="bg-celeste-night/50 border-celeste-violet/30 text-celeste-ivoire" />
            <Button data-testid="request-payout-btn" onClick={requestPayout} className="gold-btn font-semibold">Demander</Button>
          </div>
          <div className="mt-4 space-y-2">
            {(earnings?.payouts || []).map((p) => (
              <div key={p.id} className="flex justify-between text-sm text-celeste-lilas/80">
                <span>{p.amount_credits} cr</span>
                <span className="text-xs">{p.status}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="glass rounded-3xl p-6">
          <h2 className="font-serif text-lg text-celeste-ivoire">Mes dernières consultations</h2>
          <div className="mt-3 divide-y divide-celeste-violet/15">
            {consultations.slice(0, 6).map((c) => (
              <div key={c.id} className="flex justify-between py-2 text-sm">
                <span className="text-celeste-ivoire">{c.consultant_name || "Client"}</span>
                <span className="text-celeste-lilas/60">{c.status} · {Math.round((c.expert_earnings || 0) * 100) / 100} cr</span>
              </div>
            ))}
            {consultations.length === 0 && <p className="py-4 text-sm text-celeste-lilas/60">Aucune consultation.</p>}
          </div>
        </div>
      </div>

      <div className="glass mt-6 rounded-3xl p-6" data-testid="agenda-section">
        <h2 className="flex items-center gap-2 font-serif text-lg text-celeste-ivoire"><CalendarClock className="h-5 w-5 text-celeste-or" /> Mon agenda de rendez-vous</h2>
        <p className="mt-1 text-xs text-celeste-lilas/60">Publiez des créneaux ; les consultants pourront réserver une consultation future (rappel e-mail automatique).</p>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <Input data-testid="new-slot-input" type="datetime-local" value={newSlot} onChange={(e) => setNewSlot(e.target.value)} className="bg-celeste-night/50 border-celeste-violet/30 text-celeste-ivoire" />
          <Button data-testid="add-slot-btn" onClick={addSlot} className="gold-btn font-semibold"><CalendarPlus className="mr-1.5 h-4 w-4" /> Ajouter le créneau</Button>
        </div>
        <div className="mt-5 grid gap-4 lg:grid-cols-2">
          <div>
            <h3 className="mb-2 text-sm font-semibold text-celeste-or">Créneaux publiés</h3>
            <div className="space-y-2" data-testid="slots-list">
              {(profile.schedule || []).filter((s) => !s.booked && new Date(s.datetime) > new Date()).sort((a,b)=>a.datetime.localeCompare(b.datetime)).map((s) => (
                <div key={s.id} className="flex items-center justify-between rounded-xl bg-celeste-night/50 px-3 py-2 text-sm">
                  <span className="text-celeste-ivoire">{new Date(s.datetime).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })}</span>
                  <button data-testid={`remove-slot-${s.id}`} onClick={() => removeSlot(s.id)} className="text-red-300 hover:text-red-200"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              {(profile.schedule || []).filter((s) => !s.booked && new Date(s.datetime) > new Date()).length === 0 && <p className="text-sm text-celeste-lilas/60">Aucun créneau publié.</p>}
            </div>
          </div>
          <div>
            <h3 className="mb-2 text-sm font-semibold text-celeste-or">Rendez-vous réservés</h3>
            <div className="space-y-2" data-testid="expert-bookings">
              {bookings.filter((b) => b.status === "booked").map((b) => (
                <div key={b.id} className="rounded-xl bg-celeste-night/50 px-3 py-2 text-sm">
                  <p className="text-celeste-ivoire">{b.consultant_name}</p>
                  <p className="text-xs text-celeste-lilas/60">{new Date(b.scheduled_at).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })}</p>
                </div>
              ))}
              {bookings.filter((b) => b.status === "booked").length === 0 && <p className="text-sm text-celeste-lilas/60">Aucune réservation.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
